import * as _ from 'underscore';

import { siteTasksSubspaceNameToExplorableItemType } from '../../app/state/SpaceInfo';
import { FeatureFlags } from '../FeatureFlags';
import { ExplorableItemDefinitions } from '../models/ContentDefinitions';
import { FlowOutputStepRunStatus } from '../models/FlowOutputStepRunInfo';
import { RunFlowTaskInfo, RunFlowTaskStatus } from '../models/RunFlowTaskInfo';
import { FlowOutputStep } from '../ServerApi/types/FlowOutputStep';
import { FlowOutputStepRun } from '../ServerApi/types/FlowOutputStepRun';
import { FlowRun } from '../ServerApi/types/FlowRun';
import { FlowRunErrorUniqueKey } from '../ServerApi/types/FlowRunError';
import { IDataConnection } from '../ServerApi/types/IDataConnection';
import { ISchedule } from '../ServerApi/types/ISchedule';
import { ISiteNameWithId } from '../ServerApi/types/ISiteNameWithId';
import { INamedItem } from '../ServerApi/types/resourceParts';
import { RunFlowTask } from '../ServerApi/types/RunFlowTask';
import { FlowOutputStepMapper } from './FlowOutputStepMapper';
import { MapperHelper } from './MapperHelper';

export module RunFlowTaskMapper {

  const M = MapperHelper;

  export function map(
    runFlowTasks: ReadonlyArray<RunFlowTask>,
    schedule: _.Dictionary<ISchedule>,
    site: _.Dictionary<ISiteNameWithId>,
    flows: _.Dictionary<INamedItem>,

    dataConnections: _.Dictionary<IDataConnection>,
    flowOutputSteps: _.Dictionary<FlowOutputStep>,
    flowRuns: ReadonlyArray<FlowRun>,
    flowOutputStepRuns: ReadonlyArray<FlowOutputStepRun>): RunFlowTaskInfo[] {

      const flowOutputStepRunsByRun = _.groupBy(flowOutputStepRuns, 'flowRunId');
      const flowRunsGroupedByTaskId = _.groupBy(flowRuns, 'taskId');

      return _.map(runFlowTasks, (runFlowTask: RunFlowTask) => {
        const lastFlowRun = flowRunsGroupedByTaskId[runFlowTask.id] ? flowRunsGroupedByTaskId[runFlowTask.id][0] : null;
        const lastOutputStepRuns = lastFlowRun != null ? flowOutputStepRunsByRun[lastFlowRun.id] : null;
        const outputStepsForTask: FlowOutputStep[] = [];
        _.forEach(runFlowTask.flowOutputStepIds, (id) => {
          outputStepsForTask.push(flowOutputSteps[id]);
        });

        const isRunAllIncremental = !runFlowTask.runAllOutputs || runFlowTask.isRunAllIncremental === undefined ?
          null :
          runFlowTask.isRunAllIncremental;

        const flowTaskInfo: RunFlowTaskInfo = {
          id:               runFlowTask.id,
          contentType:      'runFlowTask',
          flowOutputSteps:  FlowOutputStepMapper.mapForRunFlowTask(outputStepsForTask, dataConnections, runFlowTask.flowRunSpecOutputStepParamsMap, isRunAllIncremental),
          priority:         runFlowTask.priority,
          lastRunAt:        lastFlowRun != null ? M.toDate(lastFlowRun.startedAt) : runFlowTask.lastRunAt != null ? M.toDate(runFlowTask.lastRunAt) : null,
          runNextAt:        FeatureFlags.autoSuspendFlowTasksEnabled && runFlowTask.suspended ? null : M.toDate(runFlowTask.runNextAt),
          schedule:         schedule[runFlowTask.scheduleId],
          site:             site[runFlowTask.siteId],
          flow:             flows[runFlowTask.targetId],
          flowId:           flows[runFlowTask.targetId].id,
          runAllOutputs:    runFlowTask.runAllOutputs,
          isRunAllIncremental: isRunAllIncremental,
          suspended:          runFlowTask.suspended,
          flowRunErrors:    lastOutputStepRuns != null ?
                              _.uniq(_.reduce(lastOutputStepRuns, (memo, run) => memo.concat(run.errors), []),
                                false,
                                FlowRunErrorUniqueKey) :
                            [],
          ...!!FeatureFlags.autoSuspendFlowTasksEnabled && {
            status: getRunFlowTaskStatus(runFlowTask, lastFlowRun)
          },
        };
        return flowTaskInfo;
      });
  }

  function getRunFlowTaskStatus(runFlowTask: RunFlowTask, lastFlowRun: FlowRun): RunFlowTaskStatus {
    if (runFlowTask.suspended) {
      return RunFlowTaskStatus.Suspended;
    }
    if (!lastFlowRun) {
      return RunFlowTaskStatus.Scheduled;
    }
    switch (lastFlowRun.status) {
      case FlowOutputStepRunStatus.success: {
        return RunFlowTaskStatus.Succeeded;
      }
      case FlowOutputStepRunStatus.failed: {
        return RunFlowTaskStatus.Failed;
      }
      case FlowOutputStepRunStatus.inProgress: {
        return RunFlowTaskStatus.InProgress;
      }
      default: {
        return RunFlowTaskStatus.Scheduled;
      }
    }
  }

}
